import { useMemo } from "react";
import { ArrowDown, ArrowRight, ArrowUp, Minus, Sparkles, TrendingDown, TrendingUp } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { formatNumber, getPlatformName } from "../constants";

const COPY = {
  ko: {
    title: "기간 비교",
    previous: "이전 기간",
    current: "현재 기간",
    change: "변화",
    events: "스필오버 이벤트",
    positiveRatio: "긍정 비율",
    totalLift: "총 보정 Lift",
    totalDelta: "총 관측 변화",
    strongestSource: "최강 소스",
    strongestTarget: "최강 타깃",
    pairChanges: "페어별 변화",
    source: "Source",
    target: "Target",
    prevLift: "이전 Lift",
    currLift: "현재 Lift",
    status: "상태",
    improved: "개선",
    declined: "하락",
    reversed: "반전",
    newPair: "신규",
    gonePair: "소멸",
    stable: "유지",
    noPrevData: "이전 기간에 스필오버 데이터가 없습니다.",
    noCurrData: "현재 기간에 스필오버 데이터가 없습니다.",
    noBothData: "두 기간 모두 스필오버 데이터가 없습니다.",
    pp: "%p",
  },
  en: {
    title: "Period Comparison",
    previous: "Previous",
    current: "Current",
    change: "Change",
    events: "Spillover Events",
    positiveRatio: "Positive Ratio",
    totalLift: "Total Adjusted Lift",
    totalDelta: "Total Observed Delta",
    strongestSource: "Strongest Source",
    strongestTarget: "Strongest Target",
    pairChanges: "Pair Changes",
    source: "Source",
    target: "Target",
    prevLift: "Prev Lift",
    currLift: "Curr Lift",
    status: "Status",
    improved: "Improved",
    declined: "Declined",
    reversed: "Reversed",
    newPair: "New",
    gonePair: "Gone",
    stable: "Stable",
    noPrevData: "No spillover data for the previous period.",
    noCurrData: "No spillover data for the current period.",
    noBothData: "No spillover data for either period.",
    pp: "pp",
  },
  zh: {
    title: "期间对比",
    previous: "上一期",
    current: "当前期间",
    change: "变化",
    events: "溢出事件",
    positiveRatio: "正面比例",
    totalLift: "总校正 Lift",
    totalDelta: "总观测变化",
    strongestSource: "最强来源",
    strongestTarget: "最强目标",
    pairChanges: "配对变化",
    source: "来源",
    target: "目标",
    prevLift: "上期 Lift",
    currLift: "本期 Lift",
    status: "状态",
    improved: "改善",
    declined: "下降",
    reversed: "逆转",
    newPair: "新增",
    gonePair: "消失",
    stable: "稳定",
    noPrevData: "上一期间无溢出数据。",
    noCurrData: "当前期间无溢出数据。",
    noBothData: "两个期间均无溢出数据。",
    pp: "pp",
  },
};

const pctChange = (curr, prev) => {
  if (!prev || prev === 0) return curr > 0 ? 100 : curr < 0 ? -100 : 0;
  return ((curr - prev) / Math.abs(prev)) * 100;
};

const ChangeIndicator = ({ value, suffix = "%", isPercentPoint = false }) => {
  if (value === null || value === undefined || isNaN(value)) return <span className="text-muted-foreground">-</span>;
  const isPositive = value > 0;
  const isNegative = value < 0;
  const color = isPositive ? "text-emerald-500" : isNegative ? "text-rose-500" : "text-muted-foreground";
  const Icon = isPositive ? ArrowUp : isNegative ? ArrowDown : Minus;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${color}`} data-testid="change-indicator">
      <Icon className="h-3 w-3" />
      {isPercentPoint ? `${value > 0 ? "+" : ""}${value.toFixed(1)}${suffix}` : `${value > 0 ? "+" : ""}${value.toFixed(0)}${suffix}`}
    </span>
  );
};

const MetricCompareCard = ({ label, prevValue, currValue, formatFn, changePct, isPercentPoint, testId }) => (
  <div className="rounded-2xl border border-border/60 bg-background/70 p-3 space-y-2" data-testid={testId}>
    <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground/60">{label}</p>
    <div className="flex items-end justify-between gap-2">
      <div className="space-y-0.5">
        <p className="text-[10px] text-muted-foreground/50">prev</p>
        <p className="text-sm font-medium text-muted-foreground">{formatFn ? formatFn(prevValue) : prevValue ?? "-"}</p>
      </div>
      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />
      <div className="space-y-0.5 text-right">
        <p className="text-[10px] text-muted-foreground/50">curr</p>
        <p className="text-lg font-semibold text-foreground">{formatFn ? formatFn(currValue) : currValue ?? "-"}</p>
      </div>
    </div>
    <ChangeIndicator value={changePct} isPercentPoint={isPercentPoint} suffix={isPercentPoint ? "p" : "%"} />
  </div>
);

const getStatusBadge = (status, copy) => {
  const map = {
    improved: { label: copy.improved, className: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600" },
    declined: { label: copy.declined, className: "border-rose-500/30 bg-rose-500/10 text-rose-600" },
    reversed: { label: copy.reversed, className: "border-amber-500/30 bg-amber-500/10 text-amber-600" },
    new: { label: copy.newPair, className: "border-blue-500/30 bg-blue-500/10 text-blue-600" },
    gone: { label: copy.gonePair, className: "border-zinc-500/30 bg-zinc-500/10 text-zinc-500" },
    stable: { label: copy.stable, className: "border-border/60 bg-muted/40 text-muted-foreground" },
  };
  const config = map[status] || map.stable;
  return <Badge variant="outline" className={`text-[10px] ${config.className}`}>{config.label}</Badge>;
};

export const SpilloverComparison = ({ currentAnalysis, previousAnalysis, lang = "ko", loading = false }) => {
  const copy = COPY[lang] || COPY.ko;

  const comparison = useMemo(() => {
    const curr = currentAnalysis;
    const prev = previousAnalysis;
    if (!curr && !prev) return null;

    const currSummary = curr?.summary || {};
    const prevSummary = prev?.summary || {};
    const currEvents = curr?.events?.length || 0;
    const prevEvents = prev?.events?.length || 0;
    const currPositiveRatio = currSummary.positive_effect_ratio ?? 0;
    const prevPositiveRatio = prevSummary.positive_effect_ratio ?? 0;
    const currLift = currSummary.total_adjusted_lift ?? 0;
    const prevLift = prevSummary.total_adjusted_lift ?? 0;
    const currDelta = (curr?.pair_summary || []).reduce((sum, p) => sum + (p.total_follower_delta || 0), 0);
    const prevDelta = (prev?.pair_summary || []).reduce((sum, p) => sum + (p.total_follower_delta || 0), 0);
    const currSource = currSummary.strongest_adjusted_source_platform?.platform || currSummary.strongest_source_platform?.platform || null;
    const prevSource = prevSummary.strongest_adjusted_source_platform?.platform || prevSummary.strongest_source_platform?.platform || null;
    const currTarget = currSummary.strongest_adjusted_target_platform?.platform || currSummary.strongest_target_platform?.platform || null;
    const prevTarget = prevSummary.strongest_adjusted_target_platform?.platform || prevSummary.strongest_target_platform?.platform || null;

    const currPairs = new Map((curr?.pair_summary || []).map(p => [`${p.source_platform}->${p.target_platform}`, p]));
    const prevPairs = new Map((prev?.pair_summary || []).map(p => [`${p.source_platform}->${p.target_platform}`, p]));
    const allPairKeys = new Set([...currPairs.keys(), ...prevPairs.keys()]);

    const pairChanges = [];
    for (const key of allPairKeys) {
      const cp = currPairs.get(key);
      const pp = prevPairs.get(key);
      const cLift = cp?.total_adjusted_lift ?? cp?.total_follower_delta ?? null;
      const pLift = pp?.total_adjusted_lift ?? pp?.total_follower_delta ?? null;

      let status = "stable";
      if (cp && !pp) status = "new";
      else if (!cp && pp) status = "gone";
      else if (cp && pp) {
        if ((cLift > 0 && pLift < 0) || (cLift < 0 && pLift > 0)) status = "reversed";
        else if (cLift !== null && pLift !== null && Math.abs(cLift) > Math.abs(pLift) * 1.1) status = "improved";
        else if (cLift !== null && pLift !== null && Math.abs(cLift) < Math.abs(pLift) * 0.9) status = "declined";
      }

      const [source, target] = key.split("->");
      pairChanges.push({ source, target, currentLift: cLift, previousLift: pLift, status, change: cLift !== null && pLift !== null && pLift !== 0 ? pctChange(cLift, pLift) : null });
    }

    pairChanges.sort((a, b) => {
      const order = { new: 0, reversed: 1, improved: 2, declined: 3, gone: 4, stable: 5 };
      return (order[a.status] ?? 5) - (order[b.status] ?? 5);
    });

    return {
      metrics: {
        events: { prev: prevEvents, curr: currEvents, change: pctChange(currEvents, prevEvents) },
        positiveRatio: { prev: prevPositiveRatio, curr: currPositiveRatio, change: currPositiveRatio - prevPositiveRatio },
        totalLift: { prev: prevLift, curr: currLift, change: pctChange(currLift, prevLift) },
        totalDelta: { prev: prevDelta, curr: currDelta, change: pctChange(currDelta, prevDelta) },
        strongestSource: { prev: prevSource, curr: currSource, changed: prevSource !== currSource },
        strongestTarget: { prev: prevTarget, curr: currTarget, changed: prevTarget !== currTarget },
      },
      pairChanges,
    };
  }, [currentAnalysis, previousAnalysis]);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="spillover-comparison-loading">
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-60 w-full rounded-2xl" />
      </div>
    );
  }

  if (!currentAnalysis?.data_available && !previousAnalysis?.data_available) {
    return (
      <Card data-testid="spillover-comparison-empty" className="border-border/60">
        <CardContent className="py-10 text-center">
          <TrendingUp className="mx-auto mb-3 h-8 w-8 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">{copy.noBothData}</p>
        </CardContent>
      </Card>
    );
  }

  if (!comparison) return null;

  const { metrics, pairChanges } = comparison;

  return (
    <div className="space-y-4" data-testid="spillover-comparison-root">
      {/* Phase 1: Summary Metric Comparison */}
      <Card data-testid="spillover-comparison-summary-card" className="border-primary/20 bg-gradient-to-b from-primary/4 via-background to-background">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm font-semibold">{copy.title}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCompareCard
              label={copy.events}
              prevValue={metrics.events.prev}
              currValue={metrics.events.curr}
              changePct={metrics.events.change}
              formatFn={(v) => `${v}${lang === "ko" ? "건" : lang === "zh" ? "件" : ""}`}
              testId="comparison-events"
            />
            <MetricCompareCard
              label={copy.positiveRatio}
              prevValue={metrics.positiveRatio.prev}
              currValue={metrics.positiveRatio.curr}
              changePct={metrics.positiveRatio.change}
              isPercentPoint
              formatFn={(v) => `${v}%`}
              testId="comparison-positive-ratio"
            />
            <MetricCompareCard
              label={copy.totalLift}
              prevValue={metrics.totalLift.prev}
              currValue={metrics.totalLift.curr}
              changePct={metrics.totalLift.change}
              formatFn={(v) => (v ? formatNumber(v) : "-")}
              testId="comparison-total-lift"
            />
            <MetricCompareCard
              label={copy.totalDelta}
              prevValue={metrics.totalDelta.prev}
              currValue={metrics.totalDelta.curr}
              changePct={metrics.totalDelta.change}
              formatFn={(v) => formatNumber(v)}
              testId="comparison-total-delta"
            />
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-border/60 bg-background/70 p-3" data-testid="comparison-strongest-source">
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground/60">{copy.strongestSource}</p>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{metrics.strongestSource.prev ? getPlatformName(metrics.strongestSource.prev, lang) : "-"}</span>
                <ArrowRight className="h-3 w-3 text-muted-foreground/40" />
                <span className="text-sm font-semibold text-foreground">{metrics.strongestSource.curr ? getPlatformName(metrics.strongestSource.curr, lang) : "-"}</span>
                {metrics.strongestSource.changed && metrics.strongestSource.prev && metrics.strongestSource.curr ? (
                  <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-600 text-[10px]">{lang === "ko" ? "변경" : lang === "zh" ? "变更" : "Changed"}</Badge>
                ) : null}
              </div>
            </div>
            <div className="rounded-2xl border border-border/60 bg-background/70 p-3" data-testid="comparison-strongest-target">
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground/60">{copy.strongestTarget}</p>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{metrics.strongestTarget.prev ? getPlatformName(metrics.strongestTarget.prev, lang) : "-"}</span>
                <ArrowRight className="h-3 w-3 text-muted-foreground/40" />
                <span className="text-sm font-semibold text-foreground">{metrics.strongestTarget.curr ? getPlatformName(metrics.strongestTarget.curr, lang) : "-"}</span>
                {metrics.strongestTarget.changed && metrics.strongestTarget.prev && metrics.strongestTarget.curr ? (
                  <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-600 text-[10px]">{lang === "ko" ? "변경" : lang === "zh" ? "变更" : "Changed"}</Badge>
                ) : null}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Phase 2: Pair Change Table */}
      {pairChanges.length > 0 ? (
        <Card data-testid="spillover-comparison-pair-changes-card" className="border-border/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">{copy.pairChanges}</CardTitle>
            <CardDescription>{pairChanges.length} pairs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="spillover-comparison-pair-table">
                <thead>
                  <tr className="border-b border-border/40">
                    <th className="py-2 pr-3 text-left text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground/60">{copy.source}</th>
                    <th className="py-2 pr-3 text-left text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground/60">{copy.target}</th>
                    <th className="py-2 pr-3 text-right text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground/60">{copy.prevLift}</th>
                    <th className="py-2 pr-3 text-right text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground/60">{copy.currLift}</th>
                    <th className="py-2 pr-3 text-right text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground/60">{copy.change}</th>
                    <th className="py-2 text-center text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground/60">{copy.status}</th>
                  </tr>
                </thead>
                <tbody>
                  {pairChanges.map((pair, index) => (
                    <tr key={`${pair.source}-${pair.target}`} className="border-b border-border/20 last:border-0" data-testid={`comparison-pair-row-${index}`}>
                      <td className="py-2 pr-3">
                        <Badge variant="outline" className="text-[10px]">{getPlatformName(pair.source, lang)}</Badge>
                      </td>
                      <td className="py-2 pr-3">
                        <Badge variant="outline" className="text-[10px]">{getPlatformName(pair.target, lang)}</Badge>
                      </td>
                      <td className="py-2 pr-3 text-right font-mono text-xs text-muted-foreground">
                        {pair.previousLift !== null ? formatNumber(pair.previousLift) : "-"}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono text-xs font-semibold text-foreground">
                        {pair.currentLift !== null ? formatNumber(pair.currentLift) : "-"}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {pair.change !== null ? <ChangeIndicator value={pair.change} /> : <span className="text-muted-foreground/40">-</span>}
                      </td>
                      <td className="py-2 text-center">
                        {getStatusBadge(pair.status, copy)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};
